import React from 'react';
import { buildReadingModel, boldOpenerSplit } from '../utils/revealPageText';

/**
 * ReadingCard — REVEAL-001 C2, the reading presentation of one reveal page (every width since the
 * 2026-08-10 ruling). Body text renders at >=16px Lora (the constitutional mobile-body gate).
 *
 * TWO WAYS IN, and the difference is only WHERE THE SPLIT CAME FROM:
 *   runs   — one PRINT page's pdfjs text runs; the card groups them itself. This is the original
 *            path and the one `revealReadingCardParity.test.js` gates: the card renders EXACTLY
 *            that page's extracted text, so it can never silently diverge from what prints.
 *   blocks — already-grouped blocks for one SCREEN page (founder ruling 2026-08-10, option A:
 *            pages sized to the screen reading them). The words still come from the print render
 *            via the same `buildReadingModel`; only the splitting is the screen's, and
 *            `paginateToScreen.test.js` gates that the re-splitting loses no word.
 * Passing `blocks` wins; the `runs` path is untouched, which is what keeps the parity gate honest.
 *
 * Pure React (no react-pdf import) so it unit-tests in CRA Jest.
 *
 * Props:
 *   runs        [{ str, x, y, fontSize, width? }] — one print page's pdfjs text runs
 *   blocks      [{ kind, text, continues?, continued? }] — one screen page, pre-paginated
 *   isFirstPage bool — the chapter's first page opens with a bold lead-in
 *   fill        bool — stretch to the height of its container (the paged reader's fixed stage)
 */
export default function ReadingCard({ runs, blocks, isFirstPage, fill }) {
  const model = blocks ? { blocks } : buildReadingModel(runs);
  let firstBodySeen = false;

  return (
    <div
      style={{
        // Founder ruling 2026-08-10 (walk round 2): WHITE, like the printed page — the cream card
        // on the cream app background was hard to read.
        background: '#fff',
        border: '1px solid #e4dccb',
        borderRadius: 4,
        boxShadow: '0 2px 0 #efe6d2, 0 16px 38px rgba(60,47,30,.12)',
        padding: '22px 20px 24px',
        fontFamily: "'Lora', Georgia, serif",
        color: '#2a2520',
        // Paged reader: the card IS the page, so it holds the stage's full height however much text
        // this particular page carries — otherwise every turn would resize the page under the reader.
        ...(fill ? { height: '100%', boxSizing: 'border-box', overflow: 'hidden' } : null),
      }}
    >
      {model.blocks.map((b, i) => {
        if (b.kind === 'eyebrow') {
          return (
            <div
              key={i}
              style={{
                fontSize: 13,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                color: '#5a524a',
                textAlign: 'center',
                marginBottom: 8,
              }}
            >
              {b.text}
            </div>
          );
        }
        if (b.kind === 'heading') {
          return (
            <h2
              key={i}
              style={{
                fontSize: 23,
                fontWeight: 700,
                lineHeight: 1.3,
                textAlign: 'center',
                margin: '4px 0 16px',
              }}
            >
              {b.text}
            </h2>
          );
        }
        // body. A paragraph CONTINUED from the previous screen page never re-opens with the bold
        // lead-in — that would announce a new paragraph mid-sentence.
        const isOpener = isFirstPage && !firstBodySeen && !b.continued;
        firstBodySeen = true;
        if (isOpener) {
          const { bold, rest } = boldOpenerSplit(b.text);
          return (
            <p key={i} style={bodyStyle}>
              <strong style={{ fontWeight: 700 }}>{bold}</strong>
              {rest ? ' ' + rest : ''}
            </p>
          );
        }
        return (
          <p key={i} style={bodyStyle}>
            {b.text}
          </p>
        );
      })}
    </div>
  );
}

const bodyStyle = {
  fontSize: 16, // constitutional mobile body-size gate
  lineHeight: 1.8,
  margin: '0 0 12px',
  textAlign: 'left',
};
